import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import type { SettleItemRow, SettleRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { SETTLE_STATUS_META, formatAmount, formatDisplayDate, formatDisplayTime, formatQty } from '@hq/utils';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Segmented,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import { useState } from 'react';
import { hq } from '../session';
import { PAY_METHODS } from '../components/settle/settle-enums';
import SettlePaymentTables from '../components/settle/SettlePaymentTables';
import { ellipsisCell, sumWidth } from '@hq/ui-kit';

const PAGE_SIZE = 20;

type StatusFilter =
  | 'ALL'
  | 'DRAFT'
  | 'CONFIRMED'
  | 'INVOICED'
  | 'PART_PAID'
  | 'PAID'
  | 'DISPUTED'
  | 'RE_ADJUSTED'
  | 'WRITTEN_OFF';

/** 与服务端开票门禁一致：仅已确认（含后续状态）可开票；草稿/待确认不允许。 */
const INVOICEABLE_STATUSES = ['CONFIRMED', 'PART_PAID', 'INVOICED', 'PAID'];

// 状态表统一在 `@hq/utils`：三端曾各存一份，同一 PART_PAID 一端 orange 一端 cyan
const STATUS_META = SETTLE_STATUS_META;

const FILTERS: { label: string; value: StatusFilter }[] = [
  { label: '全部', value: 'ALL' },
  { label: '草稿/待确认', value: 'DRAFT' },
  { label: '已确认', value: 'CONFIRMED' },
  { label: '已开票', value: 'INVOICED' },
  { label: '部分付款', value: 'PART_PAID' },
  { label: '已付清', value: 'PAID' },
  { label: '有争议', value: 'DISPUTED' },
  { label: '已调账', value: 'RE_ADJUSTED' },
  { label: '已核销', value: 'WRITTEN_OFF' }
];

/** 可发起争议的状态：后端 DisputeAsync 拒绝 DISPUTED / CLOSED / WRITTEN_OFF / PAID。 */
const DISPUTABLE_STATUSES = ['DRAFT', 'GENERATED', 'SENT', 'CONFIRMED', 'INVOICED', 'PART_PAID', 'RE_ADJUSTED'];

const AGING_BUCKET_LABEL: Record<string, string> = {
  NOT_DUE: '未到期',
  D0_30: '逾期 1-30 天',
  D31_60: '逾期 31-60 天',
  D61_90: '逾期 61-90 天',
  D90_PLUS: '逾期 90 天以上'
};

const currentPeriod = (): string => dayjs().format('YYYY-MM');

/** 结算中心：对账单生成 / 确认 / 开票 / 付款登记。 */
export default function SettleOps() {
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<StatusFilter>('ALL');
  const [currentId, setCurrentId] = useState<string | null>(null);

  // 五个写操作统一走 Form + rules：此前用受控裸 Input + onOk 里 message.warning，
  // 既没有字段级提示，也漏了发票代码/开票日期/备注等合同要素（与医院端 SettleInvoice 不一致）
  const [generateOpen, setGenerateOpen] = useState(false);
  const [generateForm] = Form.useForm<{
    period: Dayjs;
    supplierId: string;
    supplierName?: string;
    basis: 'SIGNED' | 'CONSUMED';
  }>();

  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [invoiceForm] = Form.useForm<{
    invoiceNo: string;
    invoiceCode?: string;
    amount: number;
    taxAmount?: number;
    invoiceAt?: Dayjs;
    remark?: string;
  }>();

  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentForm] = Form.useForm<{
    payAmount: number;
    payMethod: string;
    payAt?: Dayjs;
    voucherNo?: string;
    remark?: string;
  }>();

  const [disputeOpen, setDisputeOpen] = useState(false);
  const [disputeForm] = Form.useForm<{ reason: string; disputeAmount?: number }>();

  const [arbitrateOpen, setArbitrateOpen] = useState(false);
  const [arbitrateForm] = Form.useForm<{
    action: 'ADJUST' | 'REOPEN' | 'WRITE_OFF' | 'CLOSE';
    adjustAmount?: number;
    opinion?: string;
  }>();
  const arbitrateAction = Form.useWatch('action', arbitrateForm) ?? 'ADJUST';

  const [agingOpen, setAgingOpen] = useState(false);

  const query = useInfiniteQuery({
    queryKey: ['ops', 'settle', 'list', status],
    placeholderData: keepPreviousData,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      hq.billing.settle.list({
        lastId: pageParam,
        size: PAGE_SIZE,
        settleStatus: status === 'ALL' ? undefined : status
      }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextLastId : undefined)
  });

  const detail = useQuery({
    queryKey: ['ops', 'settle', 'detail', currentId],
    placeholderData: keepPreviousData,
    queryFn: () => hq.billing.settle.detail(currentId ?? ''),
    enabled: Boolean(currentId)
  });

  const invoices = useQuery({
    queryKey: ['ops', 'settle', 'invoices', currentId],
    placeholderData: keepPreviousData,
    queryFn: () => hq.billing.settle.invoices(currentId ?? ''),
    enabled: Boolean(currentId)
  });

  const payments = useQuery({
    queryKey: ['ops', 'settle', 'payments', currentId],
    placeholderData: keepPreviousData,
    queryFn: () => hq.billing.settle.payments(currentId ?? ''),
    enabled: Boolean(currentId)
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['ops', 'settle'] });
  };

  const aging = useQuery({
    queryKey: ['ops', 'settle', 'aging'],
    queryFn: () => hq.billing.settle.aging({ size: 200 }),
    enabled: agingOpen
  });

  // 供应商改选择器：手填 supplierId 既猜不到是谁，写错只会让生成接口报「供应商不存在」
  const supplierOptions = useQuery({
    queryKey: ['ops', 'supplier', 'options'],
    queryFn: () => hq.catalog.suppliers({ size: 200 }),
    enabled: generateOpen,
    staleTime: 5 * 60 * 1000
  });

  const dispute = useMutation({
    mutationFn: (values: { reason: string; disputeAmount?: number }) =>
      hq.billing.settle.dispute(currentId ?? '', {
        reason: values.reason.trim(),
        disputeAmount: values.disputeAmount ?? undefined
      }),
    onSuccess: async () => {
      message.success('争议已登记，对账单置为「有争议」');
      setDisputeOpen(false);
      disputeForm.resetFields();
      await refresh();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '登记争议失败')
  });

  const arbitrate = useMutation({
    mutationFn: (values: { action: 'ADJUST' | 'REOPEN' | 'WRITE_OFF' | 'CLOSE'; adjustAmount?: number; opinion?: string }) =>
      hq.billing.settle.arbitrate(currentId ?? '', {
        action: values.action,
        adjustAmount: values.action === 'ADJUST' ? values.adjustAmount : undefined,
        opinion: values.opinion?.trim() || undefined
      }),
    onSuccess: async (result) => {
      message.success(`裁定完成：${result.status}`);
      setArbitrateOpen(false);
      arbitrateForm.resetFields();
      await refresh();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '裁定失败')
  });

  const generate = useMutation({
    mutationFn: (values: { period: Dayjs; supplierId: string; supplierName?: string; basis: 'SIGNED' | 'CONSUMED' }) =>
      hq.billing.settle.generate({
        period: values.period.format('YYYY-MM'),
        supplierId: values.supplierId,
        supplierName: values.supplierName || undefined,
        basis: values.basis
      }),
    onSuccess: async (result) => {
      const fee = result.feeAmount ? `，服务费 ${formatAmount(result.feeAmount)}` : '';
      message.success(
        `已生成 ${result.bizNo}（${result.itemCount} 行，${formatAmount(result.settleAmount)}${fee}）`
      );
      setGenerateOpen(false);
      await refresh();
    },
    onError: (error) => {
      message.error(error instanceof ApiError ? error.friendlyMessage : '生成失败');
    }
  });

  const confirm = useMutation({
    mutationFn: (id: string) => hq.billing.settle.confirm(id),
    onSuccess: async () => {
      message.success('对账单已确认');
      await refresh();
      await detail.refetch();
    },
    onError: (error) => {
      message.error(error instanceof ApiError ? error.friendlyMessage : '确认失败');
    }
  });

  const invoice = useMutation({
    mutationFn: (values: {
      invoiceNo: string;
      invoiceCode?: string;
      amount: number;
      taxAmount?: number;
      invoiceAt?: Dayjs;
      remark?: string;
    }) =>
      hq.billing.settle.invoice(currentId ?? '', {
        invoiceNo: values.invoiceNo.trim(),
        invoiceCode: values.invoiceCode?.trim() || undefined,
        amount: values.amount,
        taxAmount: values.taxAmount ?? 0,
        invoiceAt: values.invoiceAt ? values.invoiceAt.format('YYYY-MM-DD') : undefined,
        remark: values.remark?.trim() || undefined
      }),
    onSuccess: async () => {
      message.success('发票已登记');
      setInvoiceOpen(false);
      invoiceForm.resetFields();
      await refresh();
      await detail.refetch();
      await invoices.refetch();
    },
    onError: (error) => {
      message.error(error instanceof ApiError ? error.friendlyMessage : '登记失败');
    }
  });

  const payment = useMutation({
    mutationFn: (values: { payAmount: number; payMethod: string; payAt?: Dayjs; voucherNo?: string; remark?: string }) =>
      hq.billing.settle.payment(currentId ?? '', {
        payAmount: values.payAmount,
        payMethod: values.payMethod,
        payAt: values.payAt ? values.payAt.format('YYYY-MM-DD') : undefined,
        voucherNo: values.voucherNo?.trim() || undefined,
        remark: values.remark?.trim() || undefined
      }),
    onSuccess: async (result) => {
      message.success(`付款已登记：${result.payNo}（${result.status}）`);
      setPaymentOpen(false);
      paymentForm.resetFields();
      await refresh();
      await detail.refetch();
      await payments.refetch();
    },
    onError: (error) => {
      message.error(error instanceof ApiError ? error.friendlyMessage : '付款登记失败');
    }
  });

  const rows = query.data?.pages.flatMap((page) => page.items) ?? [];
  const account = detail.data?.account;

  // 累计已开票：与后端 InvoiceAsync 的限额口径一致（应付 − 累计已开票）
  const invoicedTotal = (invoices.data ?? []).reduce((sum, row) => sum + (row.totalAmount ?? 0), 0);

  const columns: ColumnsType<SettleRow> = [
    {
      title: '对账单号',
      dataIndex: 'bizNo',
      width: 220,
      render: (value: string, row) => <Typography.Link onClick={() => setCurrentId(row.id)}>{value}</Typography.Link>
    },
    { title: '结算期间', dataIndex: 'settlePeriod', width: 120, render: (value: string | null) => value || '-' },
    { title: '结算对象', dataIndex: 'payeeName', width: 180, render: (value: string | null) => value || '-' },
    { title: '应结金额', dataIndex: 'settleAmount', width: 130, align: 'right', render: (value: number) => formatAmount(value) },
    {
      title: '退货扣减',
      dataIndex: 'returnAmount',
      width: 130,
      align: 'right',
      render: (value: number) =>
        value < 0 ? <Typography.Text type="danger">{formatAmount(value)}</Typography.Text> : formatAmount(value)
    },
    { title: '应付金额', dataIndex: 'payableAmount', width: 130, align: 'right', render: (value: number) => formatAmount(value) },
    { title: '已付金额', dataIndex: 'paidAmount', width: 130, align: 'right', render: (value: number) => formatAmount(value) },
    {
      title: '状态',
      dataIndex: 'settleStatus',
      width: 110,
      render: (value: string) => {
        const meta = STATUS_META[value] ?? { text: value, color: 'default' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    { title: '确认时间', dataIndex: 'confirmAt', width: 160, render: (value: string | null) => formatDisplayTime(value) }
  ];

  const itemColumns: ColumnsType<SettleItemRow> = [
    { title: '行', dataIndex: 'lineNo', width: 60 },
    { title: '业务类型', dataIndex: 'bizType', width: 120, render: (value: string | null) => value || '-' },
    { title: '业务单号', dataIndex: 'bizNo', width: 190, render: (value: string | null) => value || '-' },
    { title: '物资', dataIndex: 'materialName', ellipsis: { showTitle: false }, render: ellipsisCell('materialName') },
    { title: '数量', dataIndex: 'qty', width: 90, align: 'right', render: (value: number) => formatQty(value) },
    {
      title: '金额',
      dataIndex: 'amount',
      width: 130,
      align: 'right',
      render: (value: number) =>
        value < 0 ? <Typography.Text type="danger">{formatAmount(value)}</Typography.Text> : formatAmount(value)
    },
    {
      title: '方向',
      dataIndex: 'direction',
      width: 90,
      render: (value: number) => (value < 0 ? <Tag color="red">扣减</Tag> : <Tag color="green">计费</Tag>)
    },
    { title: '发生时间', dataIndex: 'occurAt', width: 160, render: (value: string | null) => formatDisplayTime(value) }
  ];

  return (
    <Card
      className="page-card"
      title="结算中心"
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => query.refetch()} loading={query.isFetching}>
            刷新
          </Button>
          <Button onClick={() => setAgingOpen(true)}>账龄分析</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setGenerateOpen(true)}>
            生成对账单
          </Button>
        </Space>
      }
    >
      <Segmented<StatusFilter>
        value={status}
        options={FILTERS}
        onChange={(value) => setStatus(value)}
        style={{ marginBottom: 12 }}
      />

      {query.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="对账单加载失败"
          description={query.error instanceof ApiError ? query.error.friendlyMessage : String(query.error)}
        />
      ) : null}

      <Table<SettleRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={query.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
        locale={{ emptyText: query.isError ? '加载失败，请重试' : '暂无对账单' }}
        onRow={(row) => ({ onDoubleClick: () => setCurrentId(row.id) })}
      />

      <Space style={{ marginTop: 16 }}>
        <Typography.Text type="secondary">已加载 {rows.length} 条</Typography.Text>
        {query.hasNextPage ? (
          <Button onClick={() => query.fetchNextPage()} loading={query.isFetchingNextPage}>
            加载更多
          </Button>
        ) : rows.length > 0 ? (
          <Typography.Text type="secondary">没有更多了</Typography.Text>
        ) : null}
      </Space>

      <Drawer
        title={account?.bizNo ?? '对账单详情'}
        open={currentId !== null}
        onClose={() => setCurrentId(null)}
        width={980}
      >
        {detail.isLoading ? (
          <Spin />
        ) : detail.isError ? (
          <Alert
            type="error"
            showIcon
            message="详情加载失败"
            description={detail.error instanceof ApiError ? detail.error.friendlyMessage : String(detail.error)}
          />
        ) : account ? (
          <>
            <Descriptions size="small" column={2} bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="期间">{account.settlePeriod || '-'}</Descriptions.Item>
              <Descriptions.Item label="状态">
                {STATUS_META[account.settleStatus]?.text ?? account.settleStatus}
              </Descriptions.Item>
              <Descriptions.Item label="结算对象">{account.payeeName || '-'}</Descriptions.Item>
              <Descriptions.Item label="结算依据">{account.settleBasis || '-'}</Descriptions.Item>
              <Descriptions.Item label="应结">{formatAmount(account.settleAmount)}</Descriptions.Item>
              <Descriptions.Item label="退货扣减">{formatAmount(account.returnAmount)}</Descriptions.Item>
              <Descriptions.Item label="应付">{formatAmount(account.payableAmount)}</Descriptions.Item>
              <Descriptions.Item label="已付">{formatAmount(account.paidAmount)}</Descriptions.Item>
              {/* 按「入帐为正、支出为负」如实展示差额：
                  退货红冲/调账把应付压到已付之下时应显示负数（医院应收回），
                  不能夹成 0 —— 否则账上"多付了钱"这件事在界面上完全看不见。 */}
              <Descriptions.Item label="应付未付">
                <span style={{ color: account.payableAmount - account.paidAmount < 0 ? '#cf1322' : undefined }}>
                  {formatAmount(account.payableAmount - account.paidAmount)}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="累计已开票">{formatAmount(invoicedTotal)}</Descriptions.Item>
            </Descriptions>

            <Space style={{ marginBottom: 16 }} wrap>
              {['DRAFT', 'GENERATED', 'SENT'].includes(account.settleStatus) ? (
                <Button
                  loading={confirm.isPending}
                  onClick={() =>
                    // 金额敏感操作：确认后进入可开票/可付款，必须二次确认
                    modal.confirm({
                      title: '确认对账单',
                      content: `${account.bizNo} 确认后金额即锁定，可进入开票与付款流程；如需调整须走争议流程。`,
                      okText: '确认',
                      cancelText: '取消',
                      onOk: () => confirm.mutateAsync(account.id)
                    })
                  }
                >
                  确认对账单
                </Button>
              ) : null}
              {INVOICEABLE_STATUSES.includes(account.settleStatus) ? (
                <Button
                  onClick={() => {
                    // 后端校验的是「应付 − 累计已开票」（SettleActions.InvoiceAsync），
                    // 不是「应付 − 已付」，否则第二次开票必然超限额。
                    // 输入框默认值仍夹到 0（负数发票须走红冲流程，不在本弹窗），
                    // 但真实差额已在详情里如实展示，不会被悄悄抹平。
                    invoiceForm.resetFields();
                    invoiceForm.setFieldsValue({
                      amount: Number((account.payableAmount - invoicedTotal).toFixed(2)),
                      taxAmount: 0,
                      invoiceAt: dayjs()
                    });
                    setInvoiceOpen(true);
                  }}
                >
                  登记发票
                </Button>
              ) : null}
              {/* 与服务端付款门禁一致：未确认（DRAFT/GENERATED/SENT）不允许付款 */}
              {INVOICEABLE_STATUSES.includes(account.settleStatus) ? (
                <Button
                  type="primary"
                  onClick={() => {
                    paymentForm.resetFields();
                    paymentForm.setFieldsValue({
                      payMethod: 'TRANSFER',
                      // 不夹到 0：退货冲抵会产生负差（红字），夹成 0 会让负数永远登记不进去
                      payAmount: Number((account.payableAmount - account.paidAmount).toFixed(2)),
                      payAt: dayjs()
                    });
                    setPaymentOpen(true);
                  }}
                >
                  登记付款
                </Button>
              ) : null}
              {DISPUTABLE_STATUSES.includes(account.settleStatus) ? (
                <Button
                  danger
                  onClick={() => {
                    disputeForm.resetFields();
                    setDisputeOpen(true);
                  }}
                >
                  登记争议
                </Button>
              ) : null}
              {account.settleStatus === 'DISPUTED' ? (
                <Button
                  type="primary"
                  onClick={() => {
                    arbitrateForm.resetFields();
                    arbitrateForm.setFieldsValue({ action: 'ADJUST' });
                    setArbitrateOpen(true);
                  }}
                >
                  争议裁定
                </Button>
              ) : null}
            </Space>

            <Table<SettleItemRow>
              rowKey="id"
              size="small"
              columns={itemColumns}
              dataSource={detail.data?.items ?? []}
              pagination={false}
              scroll={{ x: sumWidth(itemColumns) }}
            />

            <SettlePaymentTables
              invoices={invoices.data}
              payments={payments.data}
              invoicesLoading={Boolean(currentId) && invoices.isPending}
              paymentsLoading={Boolean(currentId) && payments.isPending}
            />
          </>
        ) : null}
      </Drawer>

      <Modal
        title="生成对账单"
        open={generateOpen}
        onCancel={() => setGenerateOpen(false)}
        onOk={() => generateForm.submit()}
        okText="生成"
        confirmLoading={generate.isPending}
        destroyOnHidden
      >
        <Form
          form={generateForm}
          layout="vertical"
          initialValues={{ period: dayjs(currentPeriod(), 'YYYY-MM'), basis: 'SIGNED' }}
          onFinish={(values) => generate.mutate(values)}
        >
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="period" label="结算期间" rules={[{ required: true, message: '请选择结算期间' }]}>
                <DatePicker picker="month" style={{ width: '100%' }} disabledDate={(current) => current.isAfter(dayjs(), 'month')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="basis" label="汇总口径" rules={[{ required: true, message: '请选择口径' }]}>
                <Select
                  options={[
                    { value: 'SIGNED', label: '按已签收配送汇总（默认）' },
                    { value: 'CONSUMED', label: '按科室消耗确认汇总（零库存）' }
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="supplierId" label="供应商" rules={[{ required: true, message: '请选择供应商' }]}>
            <Select
              showSearch
              optionFilterProp="label"
              loading={supplierOptions.isPending}
              placeholder="搜索供应商名称 / 编码"
              options={(supplierOptions.data?.items ?? []).map((row) => ({
                value: row.id,
                label: `${row.supplierName}（${row.supplierCode}）`
              }))}
              onChange={(value: string) => {
                const picked = (supplierOptions.data?.items ?? []).find((row) => row.id === value);
                generateForm.setFieldValue('supplierName', picked?.supplierName);
              }}
            />
          </Form.Item>
          <Form.Item name="supplierName" hidden>
            <Input />
          </Form.Item>
          <Typography.Text type="secondary">
            按已签收汇总取期间内已签收配送明细；按消耗汇总取未关联结算单的成本转移记录。同一期间同一供应商仅允许生成一次。
          </Typography.Text>
        </Form>
      </Modal>

      <Modal
        title="登记发票"
        open={invoiceOpen}
        onCancel={() => setInvoiceOpen(false)}
        onOk={() => invoiceForm.submit()}
        okText="登记"
        confirmLoading={invoice.isPending}
        destroyOnHidden
      >
        <Form form={invoiceForm} layout="vertical" onFinish={(values) => invoice.mutate(values)}>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="invoiceNo" label="发票号码" rules={[{ required: true, message: '请填写发票号码' }]}>
                <Input maxLength={32} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="invoiceCode" label="发票代码">
                <Input maxLength={32} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="amount" label="价税合计" rules={[{ required: true, message: '请填写价税合计' }]}>
                {/* 允许负数：退货冲抵/红冲发票是负数金额 */}
                <InputNumber precision={2} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="taxAmount" label="其中税额">
                <InputNumber min={0} precision={2} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="invoiceAt" label="开票日期">
                {/* 财务单据不能写未来日期：默认取今天，但允许任意改会造出尚未发生的开票 */}
                <DatePicker
                  style={{ width: '100%' }}
                  disabledDate={(current) => current.isAfter(dayjs(), 'day')}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="remark" label="备注">
                <Input maxLength={128} />
              </Form.Item>
            </Col>
          </Row>
          <Typography.Text type="secondary">
            累计价税合计不得超过应付含税；税额是价税合计的组成部分（不含税 = 价税合计 − 税额）。
          </Typography.Text>
        </Form>
      </Modal>

      <Modal
        title="登记付款"
        open={paymentOpen}
        onCancel={() => setPaymentOpen(false)}
        onOk={() => paymentForm.submit()}
        okText="登记"
        confirmLoading={payment.isPending}
        destroyOnHidden
      >
        <Form form={paymentForm} layout="vertical" onFinish={(values) => payment.mutate(values)}>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="payAmount"
                label="付款金额"
                rules={[{ required: true, message: '请填写付款金额' }]}
              >
                {/* 允许负数：退款/红字付款按「支出为负」登记 */}
                <InputNumber precision={2} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="payMethod" label="付款方式" rules={[{ required: true, message: '请选择付款方式' }]}>
                <Select options={PAY_METHODS} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="payAt" label="付款日期">
                {/* 同上：付款日期不允许超出今天，避免流水早于业务发生 */}
                <DatePicker
                  style={{ width: '100%' }}
                  disabledDate={(current) => current.isAfter(dayjs(), 'day')}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="voucherNo" label="凭证号">
                <Input maxLength={64} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="remark" label="备注">
            <Input maxLength={128} />
          </Form.Item>
          <Typography.Text type="secondary">付款金额不得超过应付余额，登记后自动累计已付并切换部分付款/已付清。</Typography.Text>
        </Form>
      </Modal>

      <Modal
        title="登记争议"
        open={disputeOpen}
        onCancel={() => setDisputeOpen(false)}
        onOk={() => disputeForm.submit()}
        okText="提交争议"
        okButtonProps={{ danger: true }}
        confirmLoading={dispute.isPending}
        destroyOnHidden
      >
        <Form form={disputeForm} layout="vertical" onFinish={(values) => dispute.mutate(values)}>
          <Form.Item
            name="reason"
            label="争议原因"
            rules={[{ required: true, message: '请填写争议原因（数量/质量/价格差异等）' }]}
          >
            <Input.TextArea rows={3} maxLength={300} showCount />
          </Form.Item>
          <Form.Item name="disputeAmount" label="争议金额（可选，供调账参考）">
            <InputNumber min={0} precision={2} style={{ width: '100%' }} />
          </Form.Item>
          <Typography.Text type="secondary">
            提交后对账单置为「有争议」，需运营裁定（调账 / 重开 / 核销 / 关闭）后才能继续开票付款。
          </Typography.Text>
        </Form>
      </Modal>

      <Modal
        title="争议裁定"
        open={arbitrateOpen}
        onCancel={() => setArbitrateOpen(false)}
        onOk={() => arbitrateForm.submit()}
        okText="确认裁定"
        confirmLoading={arbitrate.isPending}
        destroyOnHidden
      >
        <Form form={arbitrateForm} layout="vertical" initialValues={{ action: 'ADJUST' }} onFinish={(values) => arbitrate.mutate(values)}>
          <Form.Item name="action" label="裁定动作" rules={[{ required: true, message: '请选择裁定动作' }]}>
            <Segmented
              options={[
                { label: '调账', value: 'ADJUST' },
                { label: '重开', value: 'REOPEN' },
                { label: '核销', value: 'WRITE_OFF' },
                { label: '关闭', value: 'CLOSE' }
              ]}
            />
          </Form.Item>
          {arbitrateAction === 'ADJUST' ? (
            <Form.Item
              name="adjustAmount"
              label="调账金额（可负，正数增加应付）"
              rules={[
                { required: true, message: '调账必须填写金额' },
                {
                  validator: (_rule, value?: number) =>
                    value === undefined || value === null || value !== 0
                      ? Promise.resolve()
                      : Promise.reject(new Error('调账金额不能为 0'))
                }
              ]}
            >
              <InputNumber precision={2} style={{ width: '100%' }} />
            </Form.Item>
          ) : null}
          <Form.Item name="opinion" label="裁定意见">
            <Input.TextArea rows={2} maxLength={200} />
          </Form.Item>
          <Typography.Text type="secondary">
            调账 → 已调账（应付与折让同步累加）；重开 → 已生成；核销 → 已核销；关闭 → 已关闭。
          </Typography.Text>
        </Form>
      </Modal>

      <Modal
        title="账龄分析"
        open={agingOpen}
        onCancel={() => setAgingOpen(false)}
        footer={null}
        width={960}
      >
        {aging.isLoading ? (
          <Spin />
        ) : aging.isError ? (
          <Alert
            type="error"
            showIcon
            message="账龄加载失败"
            description={aging.error instanceof ApiError ? aging.error.friendlyMessage : String(aging.error)}
          />
        ) : aging.data ? (
          <>
            <Descriptions size="small" column={3} bordered style={{ marginBottom: 12 }}>
              <Descriptions.Item label="未结清合计">{formatAmount(aging.data.totalOutstanding)}</Descriptions.Item>
              <Descriptions.Item label="到期日口径" span={2}>
                {aging.data.hasDueDateColumn ? '按对账单到期日' : '按账期结束 + 60 天推算'}
              </Descriptions.Item>
            </Descriptions>
            <Space style={{ marginBottom: 12 }} wrap>
              {aging.data.buckets.map((bucket) => (
                <Tag key={bucket.bucket} color={bucket.bucket === 'NOT_DUE' ? 'default' : 'red'}>
                  {AGING_BUCKET_LABEL[bucket.bucket] ?? bucket.bucket}：{bucket.count} 单 / {formatAmount(bucket.amount)}
                </Tag>
              ))}
            </Space>
            <Table
              rowKey="id"
              size="small"
              pagination={false}
              scroll={{ y: 360 }}
              dataSource={aging.data.items}
              columns={[
                { title: '对账单号', dataIndex: 'bizNo', width: 200 },
                { title: '期间', dataIndex: 'settlePeriod', width: 90 },
                { title: '结算对象', dataIndex: 'payeeName', width: 160, render: (value: string | null) => value || '-' },
                { title: '未结清', dataIndex: 'outstanding', width: 120, align: 'right', render: (value: number) => formatAmount(value) },
                { title: '到期日', dataIndex: 'dueDate', width: 110, render: (value: string | null) => formatDisplayDate(value) },
                { title: '逾期天数', dataIndex: 'overdueDays', width: 100, align: 'right', render: (value: number | null) => value ?? '-' },
                {
                  title: '账龄',
                  dataIndex: 'agingBucket',
                  width: 120,
                  render: (value: string) => <Tag color={value === 'NOT_DUE' ? 'default' : 'red'}>{AGING_BUCKET_LABEL[value] ?? value}</Tag>
                }
              ]}
            />
          </>
        ) : null}
      </Modal>
    </Card>
  );
}
